import {
  createDataPackets,
  createPollPacket,
  drawQrPacket,
  parseQrPacket,
  statusMatchesPoll,
} from '../optical/qr-stream.js';
import { QrArqSender, QrUdpSender } from '../protocol/qr-arq.js';
import { drawQrCanvas } from '../optical/qr.js';

/**
 * Full-screen optical transmitter: one stable QR, or an LQP1 packet stream.
 * Only a validated STATUS bitmap may schedule missing DATA again in ARQ mode.
 */
export class SpatialScreenTransmitter {
  constructor({
    now = () => globalThis.performance?.now?.() ?? Date.now(),
    sleep = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs)),
    documentApi = globalThis.document,
    wakeLockApi = globalThis.navigator?.wakeLock,
    setTimer = (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
    clearTimer = (handle) => globalThis.clearTimeout(handle),
    requestFrame = globalThis.requestAnimationFrame?.bind(globalThis),
  } = {}) {
    this.now = now;
    this.sleep = sleep;
    this.document = documentApi;
    this.wakeLockApi = wakeLockApi;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.requestFrame = requestFrame;
    this.overlay = null;
    this.canvas = null;
    this.closeButton = null;
    this.closeHandler = null;
    this.controlTimer = null;
    this.controlTimerVersion = 0;
    this.controlGeneration = 0;
    this.controlKey = null;
    this.controlDeadlineMs = null;
    this.controlRenderPromise = null;
    this.statusWaiter = null;
    this.staticWaiter = null;
    this.abortController = null;
    this.wakeLock = null;
    this.busy = false;
    this.mode = null;
    this.lastTransfer = null;
  }

  async transmit(text, {
    mode = 'arq',
    repeats = 2,
    profile = 'screen-mono',
    chunkSize = 24,
    tokenHoldMs = 700,
    tokenMinimumDisplayMs = 240,
    statusTimeoutMs = 2500,
    maxRounds = 4,
    maxPollAttempts = 3,
    errorCorrectionLevel = 'Q',
    margin = 4,
    scale = 8,
    onProgress = () => {},
  } = {}) {
    if (this.busy) throw txError('TX_BUSY', 'Une émission QR est déjà en cours');
    if (!this.document?.createElement || !this.document?.body) {
      throw txError('DOM_UNAVAILABLE', 'Affichage plein écran indisponible');
    }
    const palette = profile === 'screen-color' ? 'pastel' : 'mono';
    const transfer = mode === 'static' ? null : createDataPackets(text, {
      chunkSize,
      errorCorrectionLevel,
      margin,
      scale,
      palette,
    });
    const holdMs = Math.max(tokenMinimumDisplayMs, tokenHoldMs);
    const startedAt = this.now();
    this.#clearControlStatus({ removeOverlay: true });
    this.busy = true;
    this.mode = mode === 'static' ? 'static' : 'transfer';
    this.abortController = new AbortController();

    try {
      this.#removeOverlay();
      this.#createOverlay({ cancellable: true });
      this.wakeLock = await this.#requestWakeLock();
      this.#assertNotAborted();
      if (mode === 'static') {
        return await this.#transmitStatic({
          text, profile, errorCorrectionLevel, scale, margin, onProgress, startedAt,
        });
      }
      if (mode === 'udp') {
        return await this.#transmitUdp({
          transfer,
          palette,
          holdMs,
          repeated: repeats,
          errorCorrectionLevel,
          scale,
          margin,
          onProgress,
          startedAt,
          profile,
        });
      }
      const arq = new QrArqSender(transfer, {
        statusTimeoutMs,
        maxRounds,
        maxPollAttempts,
      });
      const dataDisplays = new Map();
      while (true) {
        this.#assertNotAborted();
        const action = arq.nextAction(this.now());
        if (action.type === 'DATA') {
          const count = (dataDisplays.get(action.index) || 0) + 1;
          dataDisplays.set(action.index, count);
          await this.#present(action.packet, {
            title: profile === 'screen-color' ? 'QR COULEUR · DATA' : 'QR N&B · DATA',
            detail: `paquet ${action.index + 1}/${transfer.total} · ronde ${action.round}`,
            progress: (action.index + 1) / transfer.total,
            palette,
            errorCorrectionLevel,
            scale,
            margin,
          });
          onProgress({ ...action, stage: 'DATA', displayCount: count, transfer });
          await this.#abortableSleep(holdMs);
          continue;
        }

        if (action.type === 'POLL') {
          const pollPacket = createPollPacket(
            action.poll.sessionId,
            action.poll.total,
            action.poll.transferCrc,
            {
              roundId: action.poll.roundId,
              errorCorrectionLevel,
              margin,
              scale,
              palette: 'mono',
            },
          );
          const wait = this.#waitForStatus(pollPacket, Math.max(1, action.deadlineMs - this.now()));
          await this.#present(pollPacket, {
            title: action.retry ? 'POLL RÉPÉTÉ · AUCUN DATA' : 'POLL · ÉTAT RX',
            detail: `ronde ${action.round} · essai contrôle ${action.pollAttempt}/${action.maxPollAttempts}`,
            progress: 1,
            palette: 'mono',
            errorCorrectionLevel,
            scale,
            margin,
          });
          onProgress({ ...action, stage: 'POLL', packet: pollPacket, transfer });
          const status = await wait;
          if (status) {
            const accepted = arq.receiveStatus(status, this.now());
            onProgress({ ...accepted, stage: 'STATUS', status, transfer });
          }
          continue;
        }

        if (action.type === 'WAIT_STATUS') {
          await this.#abortableSleep(Math.max(1, action.deadlineMs - this.now()));
          continue;
        }

        if (action.type === 'COMPLETE') {
          const completedAt = this.now();
          const displayCounts = Object.fromEntries([...dataDisplays.entries()].sort((a, b) => a[0] - b[0]));
          const dataDisplayCount = [...dataDisplays.values()].reduce((sum, count) => sum + count, 0);
          const result = {
            ok: true,
            code: 'QR_ARQ_COMPLETE',
            profile,
            transfer,
            arq: arq.snapshot(),
            startedAt,
            completedAt,
            elapsedMs: completedAt - startedAt,
            dataDisplayCount,
            retransmittedDataCount: dataDisplayCount - transfer.total,
            displayCounts,
          };
          this.lastTransfer = result;
          onProgress({ stage: 'COMPLETE', ...result });
          return result;
        }

        if (action.type === 'FAILED') {
          throw txError(action.failureCode || 'QR_ARQ_FAILED', failureMessage(action.failureCode), {
            transfer,
            arq: arq.snapshot(),
          });
        }

        throw txError('QR_ARQ_INVALID_ACTION', `Action ARQ inconnue: ${action.type}`);
      }
    } finally {
      this.#cancelStatusWaiter();
      this.#cancelStaticWaiter();
      await this.#releaseWakeLock();
      this.#removeOverlay();
      this.abortController = null;
      this.mode = null;
      this.busy = false;
    }
  }

  /** Keep one complete envelope visible until the user closes its QR. */
  async #transmitStatic({ text, profile, errorCorrectionLevel, scale, margin, onProgress, startedAt }) {
    const palette = profile === 'screen-color' ? 'cyan' : 'mono';
    const rendered = drawQrCanvas(this.canvas, text, { palette, errorCorrectionLevel, scale, margin });
    this.overlay.querySelector('[data-tx-title]').textContent = 'QR fixe · réception non confirmée';
    this.overlay.querySelector('[data-tx-detail]').textContent = 'Scannez avec l’autre appareil, puis fermez ce QR.';
    this.overlay.querySelector('.tx-progress i').style.transform = 'scaleX(1)';
    this.overlay.style.background = '#050c0a';
    this.overlay.style.color = '#ecfff8';
    this.overlay.dataset.matrix = String(rendered.matrixSize);
    this.overlay.dataset.packetType = 'STATIC';
    this.overlay.classList.add('shown');
    await this.#abortablePaint();

    const shownAt = this.now();
    const closed = new Promise((resolve) => {
      const onAbort = () => this.#cancelStaticWaiter();
      this.staticWaiter = { resolve, onAbort };
      this.abortController.signal.addEventListener('abort', onAbort, { once: true });
    });
    onProgress({ stage: 'STATIC', mode: 'static', profile, receptionConfirmed: false, shownAt });
    await closed;
    this.#assertNotAborted();
    const completedAt = this.now();
    const result = {
      ok: true,
      code: 'QR_STATIC_SHOWN',
      mode: 'static',
      profile,
      receptionConfirmed: false,
      startedAt,
      shownAt,
      completedAt,
      elapsedMs: completedAt - startedAt,
      dataDisplayCount: 1,
    };
    this.lastTransfer = result;
    onProgress({ stage: 'COMPLETE', ...result });
    return result;
  }

  /** UDP broadcast: replay every DATA packet `repeats` times, no STATUS. */
  async #transmitUdp({
    transfer,
    palette,
    holdMs,
    repeated,
    errorCorrectionLevel,
    scale,
    margin,
    onProgress,
    startedAt,
    profile,
  }) {
    const udp = new QrUdpSender(transfer, { repeats: repeated });
    const dataDisplays = new Map();
    while (true) {
      this.#assertNotAborted();
      const action = udp.nextAction(this.now());
      if (action.type === 'DATA') {
        const count = (dataDisplays.get(action.index) || 0) + 1;
        dataDisplays.set(action.index, count);
        const totalDisplays = repeated * transfer.total;
        await this.#present(action.packet, {
          title: profile === 'screen-color' ? 'QR COULEUR · DATA' : 'QR N&B · DATA',
          detail: `paquet ${action.index + 1}/${transfer.total} · ronde ${action.round}/${repeated} · UDP`,
          progress: ((action.round - 1) * transfer.total + action.index + 1) / totalDisplays,
          palette,
          errorCorrectionLevel,
          scale,
          margin,
        });
        onProgress({ ...action, mode: 'udp', stage: 'DATA', transfer });
        await this.#abortableSleep(holdMs);
        continue;
      }

      if (action.type === 'COMPLETE') {
        const completedAt = this.now();
        const displayCounts = Object.fromEntries([...dataDisplays.entries()].sort((a, b) => a[0] - b[0]));
        const result = {
          ok: true,
          code: 'QR_UDP_SENT',
          mode: 'udp',
          profile,
          transfer,
          repeats: repeated,
          startedAt,
          completedAt,
          elapsedMs: completedAt - startedAt,
          dataDisplayCount: [...dataDisplays.values()].reduce((sum, count) => sum + count, 0),
          displayCounts,
          udp: udp.snapshot(),
        };
        this.lastTransfer = result;
        onProgress({ stage: 'COMPLETE', ...result });
        return result;
      }

      throw txError('QR_UDP_INVALID_ACTION', `Action UDP inconnue: ${action.type}`);
    }
  }

  /** Route a decoded ACK/STATUS from the local camera into the active TX. */
  acceptControl(inputPacket) {
    const packet = normalizePacket(inputPacket);
    if (!packet.ok || packet.type !== 'STATUS' || !this.statusWaiter) return false;
    if (!statusMatchesPoll(packet, this.statusWaiter.poll)) return false;
    const waiter = this.statusWaiter;
    this.statusWaiter = null;
    this.clearTimer(waiter.timer);
    this.abortController?.signal.removeEventListener('abort', waiter.onAbort);
    waiter.resolve(packet);
    return true;
  }

  /** Display a receiver STATUS on an idle device for the peer camera. */
  async showStatus(inputPacket, {
    holdMs = 3500,
    errorCorrectionLevel = 'Q',
  } = {}) {
    const packet = normalizePacket(inputPacket);
    if (!packet.ok || packet.type !== 'STATUS') throw new TypeError('STATUS LQP1 valide requis');
    if (this.busy) return { ok: false, code: 'TX_BUSY' };
    if (!Number.isFinite(holdMs) || holdMs < 0) throw new RangeError('holdMs STATUS invalide');

    const key = packet.encodedText;
    if (this.mode === 'status' && this.controlKey === key
        && this.overlay && this.controlRenderPromise) {
      const generation = this.controlGeneration;
      const renderPromise = this.controlRenderPromise;
      this.#extendControlDeadline(holdMs, generation);
      await renderPromise;
      if (generation !== this.controlGeneration || this.controlKey !== key || this.mode !== 'status') {
        return { ok: false, code: 'STATUS_REPLACED', packet };
      }
      return {
        ok: true,
        code: 'STATUS_REFRESHED',
        packet,
        reused: true,
        deadlineMs: this.controlDeadlineMs,
      };
    }

    this.#clearControlStatus({ removeOverlay: true });
    this.mode = 'status';
    this.controlKey = key;
    const generation = this.controlGeneration;
    this.#createOverlay({ cancellable: true });
    const renderPromise = this.#present(packet, {
      title: packet.missing.length ? 'STATUS · PAQUETS MANQUANTS' : 'STATUS · TRANSFERT COMPLET',
      detail: `${packet.received.length}/${packet.total} reçus · ${packet.missing.length} manquants`,
      progress: packet.received.length / packet.total,
      palette: 'mono',
      errorCorrectionLevel,
    });
    this.controlRenderPromise = renderPromise;
    this.#extendControlDeadline(holdMs, generation);
    try {
      await renderPromise;
    } catch (error) {
      if (generation === this.controlGeneration) this.#clearControlStatus({ removeOverlay: true });
      throw error;
    }
    if (generation !== this.controlGeneration || this.controlKey !== key || this.mode !== 'status') {
      return { ok: false, code: 'STATUS_REPLACED', packet };
    }
    return {
      ok: true,
      code: 'STATUS_SHOWN',
      packet,
      reused: false,
      deadlineMs: this.controlDeadlineMs,
    };
  }

  close(reason = 'user') {
    if (this.busy) {
      const error = txError('TX_CANCELLED', `Émission interrompue (${reason})`);
      this.abortController?.abort(error);
    }
    this.#cancelStatusWaiter();
    this.#clearControlStatus({ removeOverlay: true });
    if (this.busy) this.#removeOverlay();
    if (!this.busy) this.mode = null;
  }

  #createOverlay({ cancellable }) {
    const overlay = this.document.createElement('section');
    overlay.id = 'optical-tx';
    overlay.innerHTML = `
      <div class="tx-topline"><span data-tx-title></span><span data-tx-detail></span></div>
      <canvas aria-label="Paquet optique LumiChat"></canvas>
      <div class="tx-progress"><i></i></div>
      <button type="button" aria-label="Fermer la transmission">Fermer</button>`;
    this.document.body.appendChild(overlay);
    this.overlay = overlay;
    this.canvas = overlay.querySelector('canvas');
    const button = overlay.querySelector('button');
    button.hidden = !cancellable;
    this.closeButton = button;
    this.closeHandler = () => {
      if (this.mode === 'static' && this.staticWaiter) {
        this.#cancelStaticWaiter();
        this.#removeOverlay();
      } else {
        this.close('user');
      }
    };
    button.addEventListener('click', this.closeHandler);
  }

  async #present(packet, {
    title,
    detail,
    progress,
    palette,
    errorCorrectionLevel,
    scale = 8,
    margin = 4,
  }) {
    if (!this.overlay || !this.canvas) throw txError('TX_OVERLAY_MISSING', 'Écran optique fermé');
    const rendered = drawQrPacket(this.canvas, packet, { palette, errorCorrectionLevel, scale, margin });
    this.overlay.querySelector('[data-tx-title]').textContent = title;
    this.overlay.querySelector('[data-tx-detail]').textContent = detail;
    this.overlay.querySelector('.tx-progress i').style.transform = `scaleX(${clamp(progress, 0, 1)})`;
    this.overlay.style.background = '#050c0a';
    this.overlay.style.color = '#ecfff8';
    this.overlay.dataset.matrix = String(rendered.matrixSize);
    this.overlay.dataset.packetType = packet.type;
    this.overlay.classList.add('shown');
    await nextPaint(this.requestFrame);
    this.#assertNotAborted();
    return rendered;
  }

  #waitForStatus(poll, timeoutMs) {
    this.#cancelStatusWaiter();
    return new Promise((resolve) => {
      const waiter = { poll, resolve, timer: null, onAbort: null };
      this.statusWaiter = waiter;
      waiter.onAbort = () => {
        if (this.statusWaiter !== waiter) return;
        this.statusWaiter = null;
        this.clearTimer(waiter.timer);
        resolve(null);
      };
      this.abortController?.signal.addEventListener('abort', waiter.onAbort, { once: true });
      waiter.timer = this.setTimer(() => {
        if (this.statusWaiter !== waiter) return;
        this.statusWaiter = null;
        this.abortController?.signal.removeEventListener('abort', waiter.onAbort);
        resolve(null);
      }, timeoutMs);
    });
  }

  #cancelStatusWaiter() {
    if (!this.statusWaiter) return;
    this.clearTimer(this.statusWaiter.timer);
    this.abortController?.signal.removeEventListener('abort', this.statusWaiter.onAbort);
    this.statusWaiter.resolve(null);
    this.statusWaiter = null;
  }

  #cancelStaticWaiter() {
    const waiter = this.staticWaiter;
    if (!waiter) return;
    this.staticWaiter = null;
    this.abortController?.signal.removeEventListener('abort', waiter.onAbort);
    waiter.resolve();
  }

  #extendControlDeadline(holdMs, generation) {
    if (generation !== this.controlGeneration || this.mode !== 'status') return;
    const requestedDeadline = this.now() + holdMs;
    this.controlDeadlineMs = Math.max(this.controlDeadlineMs ?? requestedDeadline, requestedDeadline);
    this.clearTimer(this.controlTimer);
    const timerVersion = ++this.controlTimerVersion;

    const expire = () => {
      if (generation !== this.controlGeneration || timerVersion !== this.controlTimerVersion
          || this.mode !== 'status') return;
      this.controlTimer = null;
      const remainingMs = this.controlDeadlineMs - this.now();
      if (remainingMs > 0) {
        this.controlTimer = this.setTimer(expire, remainingMs);
        return;
      }
      this.#clearControlStatus({ removeOverlay: true });
    };
    this.controlTimer = this.setTimer(expire, Math.max(0, this.controlDeadlineMs - this.now()));
  }

  #clearControlStatus({ removeOverlay = false } = {}) {
    this.controlGeneration += 1;
    this.controlTimerVersion += 1;
    this.clearTimer(this.controlTimer);
    this.controlTimer = null;
    this.controlKey = null;
    this.controlDeadlineMs = null;
    this.controlRenderPromise = null;
    if (this.mode === 'status') this.mode = null;
    if (removeOverlay) this.#removeOverlay();
  }

  async #abortableSleep(delayMs) {
    const signal = this.abortController?.signal;
    if (!signal) return this.sleep(delayMs);
    if (signal.aborted) this.#assertNotAborted();
    await new Promise((resolve, reject) => {
      let settled = false;
      const onAbort = () => {
        if (settled) return;
        settled = true;
        reject(signal.reason || txError('TX_CANCELLED', 'Émission interrompue'));
      };
      signal.addEventListener('abort', onAbort, { once: true });
      Promise.resolve(this.sleep(delayMs)).then(() => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, (error) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', onAbort);
        reject(error);
      });
    });
    this.#assertNotAborted();
  }

  async #abortablePaint() {
    const signal = this.abortController.signal;
    this.#assertNotAborted();
    let onAbort;
    try {
      await Promise.race([
        nextPaint(this.requestFrame),
        new Promise((resolve, reject) => {
          onAbort = () => reject(signal.reason || txError('TX_CANCELLED', 'Émission interrompue'));
          signal.addEventListener('abort', onAbort, { once: true });
          if (signal.aborted) onAbort();
        }),
      ]);
    } finally {
      signal.removeEventListener('abort', onAbort);
    }
    this.#assertNotAborted();
  }

  #assertNotAborted() {
    const signal = this.abortController?.signal;
    if (signal?.aborted) throw signal.reason || txError('TX_CANCELLED', 'Émission interrompue');
  }

  async #requestWakeLock() {
    try { return await this.wakeLockApi?.request?.('screen') || null; } catch { return null; }
  }

  async #releaseWakeLock() {
    try { await this.wakeLock?.release?.(); } catch {}
    this.wakeLock = null;
  }

  #removeOverlay() {
    if (this.closeButton && this.closeHandler) {
      this.closeButton.removeEventListener?.('click', this.closeHandler);
    }
    this.closeButton = null;
    this.closeHandler = null;
    this.overlay?.remove();
    this.overlay = null;
    this.canvas = null;
  }
}

function normalizePacket(input) {
  if (typeof input === 'string') return parseQrPacket(input);
  if (input?.encodedText) return parseQrPacket(input.encodedText);
  return { ok: false, code: 'QR_PACKET_INVALID' };
}

function failureMessage(code) {
  if (code === 'QR_ARQ_STATUS_UNAVAILABLE') {
    return 'Aucun STATUS optique reçu : utilise les caméras frontales, écrans face à face';
  }
  if (code === 'QR_ARQ_MAX_ROUNDS_STATUS') return 'Trop de rondes de paquets manquants';
  if (code === 'QR_ARQ_RECEIVER_STATE_LOST') {
    return 'Le récepteur a perdu des paquets déjà confirmés. Relance le transfert après avoir vérifié le destinataire.';
  }
  return `Transfert QR interrompu (${code || 'ARQ'})`;
}

function txError(code, message, details = null) {
  const error = new Error(message);
  error.name = 'SpatialTxError';
  error.code = code;
  if (details) error.details = details;
  return error;
}

function clamp(value, minimum, maximum) { return Math.max(minimum, Math.min(maximum, value)); }

function nextPaint(requestFrame) {
  if (typeof requestFrame !== 'function') return Promise.resolve();
  return new Promise((resolve) => requestFrame(() => requestFrame(resolve)));
}
